import { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { Loader2, CalendarClock, Package, FileText, PoundSterling } from 'lucide-react';
import { useView } from '../../contexts/ViewContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  salesOrdersForecastAPI,
  type SalesOrdersForecastData,
  type SalesOrdersForecastBucket,
} from '../../lib/api/sales-orders-dashboard';
import {
  SalesOrdersDrillDown,
  type DrillDownPeriod,
} from './sales-orders/SalesOrdersDrillDown';

const BAR_COLORS = ['#0f766e', '#0284c7', '#7c3aed', '#d97706'];

/** Active forecast drill-down. `null` = closed. */
interface ForecastDrillState {
  period: DrillDownPeriod;
  title: string;
  subtitle: string;
}

type DateFormatter = (date: string | Date, options?: { format?: string; includeTime?: boolean }) => string;

/** Format a 'YYYY-MM-DD' range as a compact 'DD Mon – DD Mon' label. */
function rangeLabel(start: string, end: string, formatDate: DateFormatter): string {
  return `${formatDate(start)} – ${formatDate(end)}`;
}

/**
 * Forecast Forward tab — forward-looking view of OPEN sales-order lines
 * bucketed by promised delivery date into the next 4 weekly windows.
 *
 * Self-fetching (like SalesOrdersTab): pulls ``analytics/sales-orders-forecast/``
 * which scopes to the requesting tenant on the backend.
 */
export function ForecastForwardTab() {
  const { selectedCompanyId, selectedSiteId, viewKey } = useView();
  const { formatCurrency, formatDate } = useFormatters();

  // Active supplier scope. Company IS Site in this codebase, so a multi-site
  // helix admin (site-restricted) holds selectedCompanyId=null and switches
  // scope via selectedSiteId. Resolve from whichever is set so the forecast
  // re-scopes when the admin changes the active Poeton company in the TopBar.
  const scopeSupplierId = selectedCompanyId ?? selectedSiteId;

  const [data, setData] = useState<SalesOrdersForecastData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [drill, setDrill] = useState<ForecastDrillState | null>(null);

  // Drill into open order lines due in [start, end]. Reuses the 'open' scope
  // (status pending/partially_received). The 'open' scope deliberately ignores
  // the period's date window (see buildScopeParams), so the forecast delivery
  // window is sent via extraFilters' delivery_date_from/to instead, alongside
  // ``is_open`` (outstanding_qty > 0) — together these match the forecast
  // aggregate's line set exactly.
  const openWeekDrill = (start: string, end: string, label: string) => {
    setDrill({
      period: { start, end, label },
      title: `Open Order Lines — ${label}`,
      subtitle: `Due ${label} • outstanding qty > 0`,
    });
  };

  const openBucketDrill = (b: SalesOrdersForecastBucket) => {
    openWeekDrill(b.week_start, b.week_end, rangeLabel(b.week_start, b.week_end, formatDate));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params: Record<string, string | number> = { weeks: 4 };
    if (scopeSupplierId) params['supplier_id'] = Number(scopeSupplierId);

    salesOrdersForecastAPI
      .get(params)
      .then((resp) => { if (!cancelled) setData(resp.data); })
      .catch((err: unknown) => {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Failed to load forecast';
          setError(msg);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // viewKey changes on any scope switch (group/company/site), so the
    // forecast refetches even when only the active site changes.
  }, [scopeSupplierId, viewKey]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading forward forecast…</span>
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-destructive">{error}</CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const chartData = data.buckets.map((b) => ({
    name: b.label,
    range: rangeLabel(b.week_start, b.week_end, formatDate),
    value: b.expected_value,
    qty: b.expected_qty,
    lines: b.line_count,
    orders: b.order_count,
  }));

  // Full forecast horizon: today → last bucket end. Used by the KPI tiles
  // so they drill into every open line across the whole 4-week window.
  const horizonEnd = data.buckets[data.buckets.length - 1]?.week_end ?? data.today;
  const horizonLabel = `next ${data.weeks} week${data.weeks === 1 ? '' : 's'}`;
  const openHorizonDrill = () => openWeekDrill(data.today, horizonEnd, horizonLabel);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <CalendarClock className="h-4 w-4" />
        <span>
          Open sales-order lines due in the next 4 weeks, by promised delivery date
          {' '}(from {rangeLabel(data.today, data.buckets[data.buckets.length - 1]?.week_end ?? data.today, formatDate)})
        </span>
      </div>

      {/* Horizon totals — each tile drills into every open line in the window */}
      <div className="grid grid-cols-4 gap-4">
        <Card
          className="border-slate-200 cursor-pointer transition-colors hover:border-slate-300 hover:bg-slate-50"
          role="button"
          tabIndex={0}
          onClick={openHorizonDrill}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openHorizonDrill(); } }}
        >
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <FileText className="h-3.5 w-3.5" /> Open Order Lines
            </div>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
              {data.totals.line_count.toLocaleString()}
            </p>
            <p className="text-[11px] text-muted-foreground">across the 4-week horizon</p>
          </CardContent>
        </Card>
        <Card
          className="border-slate-200 cursor-pointer transition-colors hover:border-slate-300 hover:bg-slate-50"
          role="button"
          tabIndex={0}
          onClick={openHorizonDrill}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openHorizonDrill(); } }}
        >
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <FileText className="h-3.5 w-3.5" /> Orders
            </div>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
              {data.totals.order_count.toLocaleString()}
            </p>
            <p className="text-[11px] text-muted-foreground">distinct POs with due lines</p>
          </CardContent>
        </Card>
        <Card
          className="border-slate-200 cursor-pointer transition-colors hover:border-slate-300 hover:bg-slate-50"
          role="button"
          tabIndex={0}
          onClick={openHorizonDrill}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openHorizonDrill(); } }}
        >
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <Package className="h-3.5 w-3.5" /> Expected Quantity
            </div>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
              {data.totals.expected_qty.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </p>
            <p className="text-[11px] text-muted-foreground">outstanding units</p>
          </CardContent>
        </Card>
        <Card
          className="border-slate-200 cursor-pointer transition-colors hover:border-slate-300 hover:bg-slate-50"
          role="button"
          tabIndex={0}
          onClick={openHorizonDrill}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openHorizonDrill(); } }}
        >
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <PoundSterling className="h-3.5 w-3.5" /> Expected Value
            </div>
            <p className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">
              {formatCurrency(data.totals.expected_value)}
            </p>
            <p className="text-[11px] text-muted-foreground">outstanding commitment</p>
          </CardContent>
        </Card>
      </div>

      {/* Weekly value chart */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700">Expected Value by Week</CardTitle>
          <p className="text-[11px] text-muted-foreground">
            Outstanding value of open order lines due in each week
          </p>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} />
                <YAxis
                  tick={{ fontSize: 11, fill: '#64748b' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => v >= 1000000 ? `£${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `£${(v / 1000).toFixed(0)}k` : `£${v}`}
                />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 6 }}
                  formatter={(v: number) => formatCurrency(v)}
                  labelFormatter={(label: string, payload) => {
                    const r = (payload?.[0]?.payload as { range?: string } | undefined)?.range;
                    return r ? `${label} (${r})` : label;
                  }}
                />
                <Bar
                  dataKey="value"
                  name="Expected value"
                  radius={[4, 4, 0, 0]}
                  cursor="pointer"
                  onClick={(_d, index) => { const b = data.buckets[index]; if (b) openBucketDrill(b); }}
                >
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Weekly breakdown table */}
      <Card className="border-slate-200">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-slate-700">Weekly Breakdown</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs font-medium text-slate-500">
                  <th className="py-2 pr-4">Week</th>
                  <th className="py-2 pr-4">Date Range</th>
                  <th className="py-2 pr-4 text-right">Order Lines</th>
                  <th className="py-2 pr-4 text-right">Orders</th>
                  <th className="py-2 pr-4 text-right">Expected Qty</th>
                  <th className="py-2 text-right">Expected Value</th>
                </tr>
              </thead>
              <tbody>
                {data.buckets.map((b) => (
                  <tr
                    key={b.week_start}
                    className="border-b border-slate-100 last:border-0 cursor-pointer transition-colors hover:bg-slate-50"
                    role="button"
                    tabIndex={0}
                    onClick={() => openBucketDrill(b)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openBucketDrill(b); } }}
                  >
                    <td className="py-2 pr-4 font-medium text-slate-800">{b.label}</td>
                    <td className="py-2 pr-4 text-slate-600">{rangeLabel(b.week_start, b.week_end, formatDate)}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{b.line_count.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{b.order_count.toLocaleString()}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">
                      {b.expected_qty.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </td>
                    <td className="py-2 text-right tabular-nums">{formatCurrency(b.expected_value)}</td>
                  </tr>
                ))}
                <tr
                  className="border-t-2 border-slate-200 font-semibold text-slate-900 cursor-pointer transition-colors hover:bg-slate-50"
                  role="button"
                  tabIndex={0}
                  onClick={openHorizonDrill}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openHorizonDrill(); } }}
                >
                  <td className="py-2 pr-4" colSpan={2}>Total</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{data.totals.line_count.toLocaleString()}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">{data.totals.order_count.toLocaleString()}</td>
                  <td className="py-2 pr-4 text-right tabular-nums">
                    {data.totals.expected_qty.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </td>
                  <td className="py-2 text-right tabular-nums">{formatCurrency(data.totals.expected_value)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Drill-down into the underlying open order lines for the chosen
          window. Scoped to the active Poeton company (scopeSupplierId) and
          the forecast delivery window. */}
      <SalesOrdersDrillDown
        isOpen={drill !== null}
        onClose={() => setDrill(null)}
        scope={drill ? 'open' : null}
        period={drill?.period ?? null}
        extraFilters={drill ? {
          is_open: true,
          ...(drill.period.start ? { delivery_date_from: drill.period.start } : {}),
          ...(drill.period.end ? { delivery_date_to: drill.period.end } : {}),
        } : undefined}
        title={drill?.title ?? ''}
        subtitle={drill?.subtitle}
        supplierId={scopeSupplierId ?? undefined}
      />
    </div>
  );
}
